import { EyeClosed } from "@phosphor-icons/react";
import { Grid, Heading, Text } from "@radix-ui/themes";
import cx from "classnames";
import { ReactNode } from "react";

import { useAppSelector } from "../utils/hooks";

interface ErrorPageProps {
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  variant?: "error" | "empty" | "not-found";
  role?: "alert" | "status";
}

export function ErrorPage({
  title,
  description,
  children,
  icon,
  variant = "empty",
  role = "status",
}: ErrorPageProps) {
  const { drawerOpen } = useAppSelector((state) => state.ui);

  // Determine if this is an error state for accessibility
  const isErrorState = variant === "error";
  const finalRole = isErrorState ? "alert" : role;

  return (
    <Grid
      gap="4"
      className={cx("mx-auto mt-40 max-w-prose text-center", {
        "ml-[calc(50%)] -translate-x-1/2": drawerOpen,
      })}
      role={finalRole}
      aria-live={isErrorState ? "assertive" : "polite"}
      data-testid={`error-page-${variant}`}
    >
      {icon || (
        <EyeClosed
          size={48}
          className="mx-auto text-puntt-blue-9"
          aria-hidden="true"
          data-testid="error-page-default-icon"
        />
      )}

      <Heading
        size="6"
        className="text-puntt-neutral-gray-9"
        data-testid="error-page-title"
      >
        {title}
      </Heading>

      {description && (
        <Text
          size="3"
          className="text-puntt-neutral-gray-9"
          data-testid="error-page-description"
        >
          {description}
        </Text>
      )}

      {children && <div data-testid="error-page-children">{children}</div>}
    </Grid>
  );
}
